import { createAppI18n, sharedTranslations, type TKeyOf } from '@repo/i18n'
import { clientTranslations } from '@/routes/-translations/client-translations'
import { auditTranslations } from '@/routes/(private)/(shell)/admin/audit/-translations/audit-translations'
import { adminLinksTranslations } from '@/routes/(private)/(shell)/admin/links/-translations/admin-links-translations'
import { peopleTranslations } from '@/routes/(private)/(shell)/admin/people/-translations/people-translations'
import { policiesTranslations } from '@/routes/(private)/(shell)/admin/policies/-translations/policies-translations'
import { sessionsTranslations } from '@/routes/(private)/(shell)/admin/sessions/-translations/sessions-translations'
import { groupsTranslations } from '@/routes/(private)/(shell)/groups/-translations/groups-translations'
import { linksTranslations } from '@/routes/(private)/(shell)/links/-translations/links-translations'
import { profileTranslations } from '@/routes/(private)/(shell)/settings/profile/-translations/profile-translations'
import { publicLinkTranslations } from '@/routes/s/$token/-translations/public-link-translations'
import { commonTranslations } from '@/translations/common-translations'

/**
 * App-owned i18n facade. The engine and the shared `shared` namespace (text
 * built into `@repo/ui` components) live in `@repo/i18n`; the app's own
 * `common` namespace and feature namespaces live here. App code keeps importing
 * from `@/lib/i18n`.
 */
const resources = {
	en: {
		common: commonTranslations.en,
		shared: sharedTranslations.en,
		client: clientTranslations.en,
		'admin-people': peopleTranslations.en,
		groups: groupsTranslations.en,
		'admin-audit': auditTranslations.en,
		'admin-links': adminLinksTranslations.en,
		'admin-policies': policiesTranslations.en,
		'admin-sessions': sessionsTranslations.en,
		links: linksTranslations.en,
		profile: profileTranslations.en,
		'public-link': publicLinkTranslations.en
	},
	it: {
		common: commonTranslations.it,
		shared: sharedTranslations.it,
		client: clientTranslations.it,
		'admin-people': peopleTranslations.it,
		groups: groupsTranslations.it,
		'admin-audit': auditTranslations.it,
		'admin-links': adminLinksTranslations.it,
		'admin-policies': policiesTranslations.it,
		'admin-sessions': sessionsTranslations.it,
		links: linksTranslations.it,
		profile: profileTranslations.it,
		'public-link': publicLinkTranslations.it
	}
} as const

const appI18n = createAppI18n(resources, { defaultNS: 'common' })

export const { useTranslation, Trans, translateApp } = appI18n
export type TFunction = typeof appI18n.i18n.t

/**
 * All translation keys with namespace prefix (e.g.
 * `shared:http_errors.default`).
 */
export type TKey = TKeyOf<(typeof resources)['en']>

const i18n = appI18n.i18n

export default i18n

export {
	APP_LANGUAGES,
	type AppLanguage,
	type AppLanguageCode,
	changeAppLanguage,
	DEFAULT_LANGUAGE,
	detectAppLanguageFromRequest,
	getInitialAppLanguage,
	isAppLanguageCode,
	onAppLanguageChange,
	resolveAppLanguageCode,
	syncAppLanguage,
	translateKey
} from '@repo/i18n'
